// Product switcher ("app drawer"): a compact launcher popover under the header
// grid button. Same product tiles as the Overview, smaller. While open, the
// digit keys 1–6 (and ⌘/Ctrl+digit, where the browser lets a page claim them)
// jump straight to a product; Esc / backdrop closes. AppShell also binds
// ⌘1–⌘6 globally so the shortcut works without opening the drawer.

import { forwardRef, useEffect, useMemo, useRef } from "react";
import { preloadRoute } from "@/app/routePreload";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/lib/auth/AuthProvider";
import { visibleApps, type AppId, type ConsoleApp } from "@/app/products";
import { Kbd, ProductIcon } from "@/app/console/ui";

interface AppDrawerProps {
  open: boolean;
  onClose: () => void;
  activeAppId: AppId;
}

export function AppDrawer({ open, onClose, activeAppId }: AppDrawerProps) {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const firstRef = useRef<HTMLButtonElement>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const apps = useMemo(() => visibleApps(roles), [roles]);
  const products = useMemo(() => apps.filter((a) => a.kind === "product"), [apps]);
  const manage = useMemo(() => apps.filter((a) => a.kind === "manage"), [apps]);
  const targets = useMemo(() => [...products, ...manage], [products, manage]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (/^[1-9]$/.test(e.key) && !e.altKey && !e.shiftKey) {
        const app = targets[Number(e.key) - 1];
        if (app) {
          e.preventDefault();
          onClose();
          navigate(app.home);
        }
      }
    }
    document.addEventListener("keydown", onKey);
    const id = window.setTimeout(() => dialogRef.current?.focus({ preventScroll: true }), 20);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(id);
    };
  }, [open, onClose, navigate, targets]);

  if (!open) return null;

  function openApp(app: ConsoleApp) {
    onClose();
    navigate(app.home);
  }

  return (
    <div
      className="fixed inset-0 z-menu"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-label="Switch product"
        className="focus:outline-none absolute right-3 top-[60px] w-[500px] max-w-[calc(100vw-1.5rem)] select-none rounded-card border border-line-strong bg-surface-3 p-3 shadow-[0_24px_64px_rgba(0,0,0,0.55)] sm:right-4"
      >
        <div className="flex items-center justify-between px-1 pb-2">
          <span className="pb-micro text-text-3">Products</span>
          <span className="hidden text-[11px] text-text-3 sm:inline coarse:hidden">Press a number to jump</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {products.map((app, i) => (
            <AppTile
              key={app.id}
              ref={i === 0 ? firstRef : undefined}
              app={app}
              index={i + 1}
              active={app.id === activeAppId}
              onClick={() => openApp(app)}
            />
          ))}
        </div>

        {manage.length > 0 && (
          <>
            <div className="pb-micro mt-3 px-1 pb-2 text-text-3">Manage</div>
            <div className="space-y-1">
              {manage.map((app, i) => (
                <AppTile
                  key={app.id}
                  app={app}
                  index={products.length + i + 1}
                  active={app.id === activeAppId}
                  onClick={() => openApp(app)}
                  wide
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


const AppTile = forwardRef<
  HTMLButtonElement,
  { app: ConsoleApp; index: number; active: boolean; onClick: () => void; wide?: boolean }
>(function AppTile({ app, index, active, onClick, wide }, ref) {
  const onWarm = () => preloadRoute(app.home);
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseEnter={onWarm}
      onFocus={onWarm}
      onPointerDown={onWarm}
      aria-current={active ? "true" : undefined}
      className={[
        "group relative flex items-start gap-3 rounded-control border p-2.5 text-left transition-[background-color,border-color,transform] duration-fast ease-pb focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand/70",
        active
          ? "border-line-strong bg-white/[0.06]"
          : "border-transparent hover:-translate-y-px hover:border-line-strong hover:bg-white/[0.04]",
        wide ? "coarse:min-h-11" : "min-h-[64px]",
      ].join(" ")}
    >
      {active && <span className="absolute inset-y-2 left-0 w-0.5 rounded-r transition-opacity duration-fast group-focus-visible:opacity-0" style={{ backgroundColor: app.accent }} aria-hidden="true" />}
      <ProductIcon icon={app.icon} accent={app.accent} size="sm" className="mt-0.5 self-start" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold leading-5 text-text-1">{app.name}</span>
          <Kbd className="shrink-0 coarse:hidden">⌘{index}</Kbd>
        </span>
        <span className="line-clamp-2 block text-[11px] leading-4 text-text-3">{app.tagline}</span>
      </span>
    </button>
  );
});
